import { format } from "date-fns";
import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import Box from "react-raster";
import styled from "styled-components";
import {
  Button,
  ConfirmationDialog,
  DatePicker,
  Icon,
  Input,
  Modal,
  MultiSelect,
  Option,
  Spacer,
  Text,
  Radio
} from "src/components";
import { theme } from "src/config";
import { Space } from "src/models";
import { getRoutePath, Page } from "src/routes";
import { AuthStore, RoutingStore, SpaceStore } from "src/stores";
import { ProjectTheme } from "src/types";
import { Card } from "./card";
import toast from 'react-hot-toast';
import { useClickOutside } from "src/util/util";

export interface IProjectCardProps {
  data:          Space;
  authStore?:    AuthStore;
  spaceStore?:   SpaceStore;
  routingStore?: RoutingStore;
}

export const ProjectCard = inject("spaceStore", "routingStore", "authStore")(observer((props: IProjectCardProps): JSX.Element => {
  const { authStore, spaceStore, routingStore, data: project } = props;
  const { t, i18n } = useTranslation();

  const cardRef = React.useRef<HTMLDivElement>(null);

  // @TODO move this state inside the confirmation dlg 
  const [confirmationInput,          setConfirmationInput]          = React.useState<string>("");
  const [showConfirmationInputError, setShowConfirmationInputError] = React.useState<boolean>(false);
  const [showConfirmDialog,          setShowConfirmDialog]          = React.useState(false);

  const themes: Option[] = Object.values(ProjectTheme).map((t) => ({
    value: t,
    label: t
  }));
  
  const enterButtonAction = (): void => {
    if (!currentUser.inSpace(project.id)) return;

    spaceStore.changeProject(project.id);
    routingStore.push(
      getRoutePath(Page.ProjectSettings, { id: project.id, view: 'settings' })
    );
  };

  const { getCurUser: currentUser } = authStore;

  useClickOutside(cardRef, () => {
    const dateModalOpen = localStorage.getItem('_op_date_picker_modal_open') === 'true';
    if(spaceStore.getActive && project.id === spaceStore.getActive.id && !dateModalOpen && !showConfirmDialog) {
      spaceStore.setActive(null, true);
    }
  });

  return (
    <Card
      ref      = {cardRef}
      key      = {project.id}
      editable = {currentUser.canEdit}
      expanded = {Boolean(
        spaceStore.getActive && project.id === spaceStore.getActive.id
      )}
      // action={authStore.getCurUser.canEditSpace(project.id) ?
      //   <ActionItem>
      //     {currentUser.inSpace(project.id) && (
      //       <Icon size="xxlarge" iconId="settings" color={theme.color.primary7} alternateFill="#fff" />
      //     )}
      //   </ActionItem>
      //   : undefined
      // }
      // onAction={async () => {
      //   spaceStore.setActive(project)
      // }}
      onClick  = {(e) => spaceStore.setActive(project) }
      body     = {
        <>
          <Text size="default" truncated>{project.title}</Text>
          <CardBodyContainer>
          {authStore.getCurUser.getRole(project.id) && authStore.getCurUser.canEditSpace(project.id) && (
            <StyledButton variant='outlined' size='small' onClick={() => enterButtonAction()}>{t('enter')}</StyledButton>
          )}
          </CardBodyContainer>
        </>
      }
      height          = "auto"
      width           = "30rem"
      backgroundColor = '#FFFFFF'
      expandedBody    = {
        <CardContainer>
          <ProjectContainerHead>
            <Input
              editOnFocus
              autoFocus = {false}
              value    = {project.title || ""}
              onBlur   = {() => project.save()}
              label    = {t("project_name")}
              onChange = {(val) => {
                project.set("title", val);
              }}
            />
          </ProjectContainerHead>
          {/* <ProjectCardContent>
            <MultiSelect
              label    = {t('theme')}
              options  = {themes}
              selected = {[{ value: project.theme, label: project.theme }]}
              onChange = {(item: Option) => {
                project.set("theme", item.value, true);
              }}
              disableSearch
              singleSelect
              forceSelect
            />
          </ProjectCardContent> */}
          <InputContainer>
            <StyledDatePicker
              color    = "red"
              label    = {t('start_date')}
              value    = {project.start_date || new Date()}
              onChange = {(date) => {
                project.set("start_date", date, true);
              }}
            />
            <Spacer size="small" />
            <StyledDatePicker
              label    = {t('end_date')}
              value    = {project.end_date || new Date()}
              onChange = {(date) => {
                project.set("end_date", date, true);
              }}
            />
          </InputContainer>
          <RadioContainer>
            <Radio
              checked = {project.member_listing === "name"}
              value   = {t("display_by_name")}
              onClick = {() => project.set("member_listing", "name", true)}
            />
            <Radio
              checked = {project.member_listing === "title"}
              value   = {t("display_by_title")}
              onClick = {() => project.set("member_listing", "title", true)}
            />
          </RadioContainer>
          <ButtonContainer>
            <Modal show={showConfirmDialog}>
              {/* <ConfirmationDialog
                onCancel  = {() => setShowConfirmDialog(false)}
                label     = {t("confirm_action")}
                message   = {t("confirm_action_project_message", {item: project.title})}
                onConfirm = {() => {
                  project.remove();
                  spaceStore.setActive(null);
                  toast.success(t('project_deleted'))

                }}
              /> */}
              <ConfirmationDialog
                onConfirm={async () => {
                  try {
                    await project.remove();
                    spaceStore.setActive(null);
                    toast.success(t("project_deleted"), {
                      icon: <Icon size="medium" iconId="delete" />
                    });
                  } catch (err) {
                    console.error("Failed to remove project", project);
                  }
                }}
                onCancel = {() => setShowConfirmDialog(false)}
                label    = {t("confirm_action")}
                message  = {t("confirm_action_delete_space_message", { space: project.title, type: t("project") })}
                disabled = {confirmationInput !== project.title}
                content  = {<Input value={confirmationInput} showError={showConfirmationInputError} onBlur={() => setShowConfirmationInputError(!showConfirmationInputError)}
                onChange={(value) => setConfirmationInput(value)} type="text" errorMessage={t("invalid_space_name", { type: t("project") })} />}
              />
            </Modal>
            <Button variant="icon" onClick={() => setShowConfirmDialog(true)}>
              <Icon iconId="delete" size="large" color={theme.color.grayDark} />
            </Button>
          </ButtonContainer>
        </CardContainer>
      }
      footer={
        <Box>
          <Text color={"grayDefault"} size="small">
            {`${project.start_date ? format(project.start_date, "yy.MM.dd") : ""
              } ${project.end_date
                ? " - " + format(project.end_date, "yy.MM.dd")
                : ""
              }`}
          </Text>
          {authStore.getCurUser.getRole(project.id) && (
            <Text size="small">{t(authStore.getCurUser.getRole(project.id))}</Text>
          )}
          {!authStore.getCurUser.getRole(project.id) && (
            <Text size="small">{`${t("your_not_in_this_project")}`}</Text>
          )}
        </Box>
      }
    />
  );
}));


const InputContainer = styled.div`
  display:         flex;
  width:           100%;
  justify-content: space-between;
`;

const ActionItem = styled.div`
  :hover {
    cursor: pointer;
    svg {
      transform: rotate(40deg);
    }
  }
`;

const CardBodyContainer = styled.div`
  display:         flex;
  flex-direction:  column;
  justify-content: center;
  align-items:     center;
`

const RadioContainer = styled.div`
  display:        flex;
  flex-direction: column;
`;

const StyledDatePicker = styled(DatePicker)`
  width:   2rem;
  padding: 0rem;
`;

const ProjectContainerHead = styled.div`
  flex-direction: row;
  align-items:    center;
  display:        flex;
  gap:            1rem;
`;

const CardContainer = styled.div`
  display:        flex;
  flex-direction: column;
  gap:            1rem;
`;

const ProjectCardContent = styled.div`
  display:        grid;
  flex-direction: column;
  gap:            1rem;
  margin:         1rem 0rem;
`;

const StyledButton = styled(Button)`
  padding-left:  3rem;
  padding-right: 3rem;
`

const ButtonContainer = styled.div`
  display:         flex;
  width:           100%;
  justify-content: space-between;
  flex-direction:  row-reverse;
`;